import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import {
  FeatherCheck,
  FeatherDownload,
  FeatherSmartphone,
  FeatherWifi,
  FeatherWifiOff,
} from "@subframe/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { usePWAInstall } from "@/hooks/use-pwa-install";

export const Route = createFileRoute("/install")({
  component: InstallPage,
});

function InstallPage() {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const navigate = useNavigate();

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      navigate({ to: "/dashboard" });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-default-background p-4">
      <div className="w-full max-w-md animate-fade-in flex flex-col items-center gap-8">
        {/* Logo */}
        <div className="flex flex-col items-center gap-4">
          <IconWithLogoBackground variant="brand" size="x-large" />
          <div className="flex flex-col items-center gap-1">
            <span className="text-heading-1 font-heading-1 text-default-font">
              Nursery Audit
            </span>
            <span className="text-body font-body text-subtext-color">
              Install the app for the best experience
            </span>
          </div>
        </div>

        {/* Install Status Card */}
        {isInstalled ? (
          <div className="flex w-full flex-col items-center gap-6 rounded-lg border border-solid border-success-200 bg-success-50 p-6">
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-100">
                <FeatherCheck className="h-6 w-6 text-success-700" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  App Installed!
                </span>
                <span className="text-body font-body text-subtext-color text-center">
                  You can now use Nursery Audit from your home screen
                </span>
              </div>
            </div>
            <Button
              className="w-full"
              variant="brand-primary"
              size="large"
              onClick={() => navigate({ to: "/dashboard" })}
            >
              Open App
            </Button>
          </div>
        ) : isInstallable ? (
          <div className="flex w-full flex-col items-center gap-6 rounded-lg border border-solid border-secondary-beige-200 bg-white p-6">
            <div className="flex flex-col items-center gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Install Now
              </span>
              <span className="text-body font-body text-subtext-color text-center">
                Add to your home screen for quick access
              </span>
            </div>
            <div className="flex w-full flex-col items-center gap-3">
              <Button
                className="w-full"
                variant="brand-primary"
                size="large"
                icon={<FeatherDownload />}
                onClick={handleInstall}
              >
                Install App
              </Button>
              <Button
                className="w-full"
                variant="neutral-tertiary"
                size="large"
                onClick={() => navigate({ to: "/dashboard" })}
              >
                Continue in browser
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex w-full flex-col items-center gap-6 rounded-lg border border-solid border-secondary-beige-200 bg-white p-6">
            <div className="flex flex-col items-center gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Install Instructions
              </span>
              <span className="text-body font-body text-subtext-color text-center">
                Add this app to your home screen
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  On iPhone/iPad:
                </span>
                <ol className="list-decimal list-inside space-y-1 text-body font-body text-subtext-color">
                  <li>Tap the Share button (box with arrow)</li>
                  <li>Scroll down and tap &quot;Add to Home Screen&quot;</li>
                  <li>Tap &quot;Add&quot; to confirm</li>
                </ol>
              </div>
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  On Android:
                </span>
                <ol className="list-decimal list-inside space-y-1 text-body font-body text-subtext-color">
                  <li>Tap the menu (three dots)</li>
                  <li>
                    Tap &quot;Add to Home screen&quot; or &quot;Install
                    app&quot;
                  </li>
                  <li>Tap &quot;Add&quot; to confirm</li>
                </ol>
              </div>
            </div>
            <Button
              className="w-full"
              variant="neutral-primary"
              size="large"
              onClick={() => navigate({ to: "/dashboard" })}
            >
              Continue in browser
            </Button>
          </div>
        )}

        {/* Features Card */}
        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-secondary-beige-200 bg-white p-6">
          <div className="flex w-full items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50">
              <FeatherSmartphone className="h-5 w-5 text-brand-600" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-body-bold font-body-bold text-default-font">
                Works like a native app
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Full-screen experience without browser UI
              </span>
            </div>
          </div>
          <div className="flex w-full items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50">
              <FeatherWifiOff className="h-5 w-5 text-brand-600" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-body-bold font-body-bold text-default-font">
                Works offline
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Complete checklists even without internet
              </span>
            </div>
          </div>
          <div className="flex w-full items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50">
              <FeatherWifi className="h-5 w-5 text-brand-600" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-body-bold font-body-bold text-default-font">
                Auto-syncs when online
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Your data syncs automatically when connected
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
